import { useEffect, useRef } from 'react'

/** Keep keyboard navigation inside an open dialog and return focus on close. */
export function useDialogAccessibility<T extends HTMLElement = HTMLDivElement>(onClose?: () => void) {
  const dialogRef = useRef<T>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focusable = () => [...(dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
    ) || [])].filter((element) => element.getClientRects().length > 0)
    const first = dialogRef.current?.querySelector<HTMLElement>('[data-dialog-autofocus]') || focusable()[0]
    ;(first || dialogRef.current)?.focus()

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeRef.current) {
        event.preventDefault()
        event.stopPropagation()
        closeRef.current()
      }
      if (event.key !== 'Tab') return
      const elements = focusable()
      if (!elements.length) {
        event.preventDefault()
        dialogRef.current?.focus()
      } else if (event.shiftKey && (document.activeElement === elements[0] || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault()
        elements[elements.length - 1].focus()
      } else if (!event.shiftKey && (document.activeElement === elements[elements.length - 1] || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault()
        elements[0].focus()
      }
    }
    document.addEventListener('keydown', handleKey, true)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKey, true)
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true })
    }
  }, [])
  return dialogRef
}
